import { useField } from 'formik';
import { Suspense, useEffect } from 'react';
import { graphql, useQueryLoader } from 'react-relay';

import usePaginatorSearchOverlayQueryLoader from '../../../hooks/usePaginatorSearchOverlayQueryLoader';
import {
  StoreStuffPaginatorSearchOverlayField_storeStuffsQuery,
  StoreStuffPaginatorSearchOverlayField_storeStuffsQuery$data,
  StoreStuffPaginatorSearchOverlayField_storeStuffsQuery$variables,
} from '../../../relay/__generated__/StoreStuffPaginatorSearchOverlayField_storeStuffsQuery.graphql';
import { isNullable } from '../../../utils/is';
import EmptyState from '../../core/EmptyState';
import ItemList from '../../core/ItemList';
import PreloadedQueryRenderer from '../../core/PreloadedQueryRenderer';
import ScrollContainer from '../../core/ScrollContainer';
import SearchOverlayField, { SearchOverlayFieldProps } from '../../core/SearchOverlayField';
import Spinner from '../../core/Spinner';
import Text from '../../core/Text';
import View from '../../core/View';
import StoreStuffPaginator from '../StoreStuffPaginator';

const storeStuffsForStoreStuffPaginatorSearchOverlayField = graphql`
  query StoreStuffPaginatorSearchOverlayField_storeStuffsQuery(
    $filters: StoreStuffFilter
    $order: StoreStuffOrder
    $first: Int
    $after: String
  ) {
    storeStuffs(filters: $filters, order: $order, first: $first, after: $after) {
      edges {
        node {
          id
          title
          stuff {
            id
            title
          }
        }
      }
    }
    ...StoreStuffPaginator_query @arguments(filters: $filters, order: $order, first: $first, after: $after)
  }
`;

type Props = {
  variables?: StoreStuffPaginatorSearchOverlayField_storeStuffsQuery$variables;
} & Omit<
  SearchOverlayFieldProps<
    StoreStuffPaginatorSearchOverlayField_storeStuffsQuery$data['storeStuffs']['edges'][number]['node']
  >,
  'renderItemList'
>;

const StoreStuffPaginatorSearchOverlayField = ({ name = '', value: propValue, variables, ...props }: Props) => {
  const [{ value: baseValue }] = useField<string>({ name });
  const value = !isNullable(propValue) ? propValue : baseValue;

  const [valueQueryReference, loadValueQuery, disposeValueQuery] =
    useQueryLoader<StoreStuffPaginatorSearchOverlayField_storeStuffsQuery>(
      storeStuffsForStoreStuffPaginatorSearchOverlayField,
    );
  useEffect(() => {
    loadValueQuery({ filters: { id_Exact: value ?? null } });
    return () => disposeValueQuery();
  }, [value, loadValueQuery, disposeValueQuery]);

  const [searchOverlayFieldProps, queryReference] =
    usePaginatorSearchOverlayQueryLoader<StoreStuffPaginatorSearchOverlayField_storeStuffsQuery>(
      storeStuffsForStoreStuffPaginatorSearchOverlayField,
      variables,
    );

  return valueQueryReference ? (
    <PreloadedQueryRenderer
      query={storeStuffsForStoreStuffPaginatorSearchOverlayField}
      queryReference={valueQueryReference}
    >
      {({ storeStuffs }) => (
        <SearchOverlayField
          value={
            storeStuffs?.edges
              .map(({ node: { title, stuff } }) => `${title}${stuff ? ` / ${stuff.title}` : ''}`)
              .join(', ') || ''
          }
          name={name}
          renderItemList={({ containerRef, handleSelect }, itemProps) =>
            queryReference ? (
              <Suspense fallback={<Spinner sx={{ paddingY: 3 }} />}>
                <PreloadedQueryRenderer
                  query={storeStuffsForStoreStuffPaginatorSearchOverlayField}
                  queryReference={queryReference}
                >
                  {(queryData) => (
                    <StoreStuffPaginator fragmentReference={queryData}>
                      {(data, { hasNext, isLoadingNext, loadMore }) => (
                        <ScrollContainer
                          ref={containerRef as React.RefObject<HTMLDivElement>}
                          onScrollReachEnd={() => {
                            if (hasNext && !isLoadingNext) loadMore(20);
                          }}
                        >
                          <ItemList
                            items={data.storeStuffs.edges}
                            renderItem={({ node: { title, stuff } }) => (
                              <Text sx={{ fontSize: 1 }}>{`${title}${stuff ? ` / ${stuff.title}` : ''}`}</Text>
                            )}
                            renderItemWrapper={(children, { node }) => (
                              <View
                                {...itemProps}
                                key={node.id}
                                onClick={(e) => handleSelect(e, node)}
                                onKeyDown={(e) => handleSelect(e, node)}
                              >
                                {children}
                              </View>
                            )}
                            emptyState={
                              <View sx={{ paddingY: 3 }}>
                                <EmptyState
                                  title={'검색 결과가 없어요'}
                                  description={'다른 검색어로 다시 시도해주세요.'}
                                />
                              </View>
                            }
                          />
                          <View>{isLoadingNext ? <Spinner size={'small'} /> : null}</View>
                        </ScrollContainer>
                      )}
                    </StoreStuffPaginator>
                  )}
                </PreloadedQueryRenderer>
              </Suspense>
            ) : null
          }
          {...searchOverlayFieldProps}
          {...props}
        />
      )}
    </PreloadedQueryRenderer>
  ) : (
    <Spinner size={'small'} />
  );
};

export default StoreStuffPaginatorSearchOverlayField;
